import React, { useEffect, useState } from 'react';
import { useStore } from 'effector-react';
import { Box, TextField, MenuItem } from '@mui/material';
import { toast } from 'react-toastify';

import { getStations } from '../../../../services/stationsService';
import { Station } from '../../../../types/stations.types';
import { createPrinterFx } from '../../../../stores/PrintersStore/PrintersStore.effects';
import { PrintersStore } from '../../../../stores/PrintersStore/PrintersStore';
import SimpleModal from '../../../../components/SimpleModal/SimpleModal';

interface Props {
  TriggerComponent: React.ReactElement<any>;
}

const CreatePrinterModal: React.FC<Props> = ({ TriggerComponent }) => {
  const { loading } = useStore(PrintersStore);
  const [open, setOpen] = useState(false);

  const [stations, setStations] = useState<Station[]>([]);
  const [fetchingStations, setFetchingStations] = useState(false);

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [stationId, setStationId] = useState('');
  const [gasPumpsStr, setGasPumpsStr] = useState('');

  useEffect(() => {
    const loadStations = async () => {
      setFetchingStations(true);
      try {
         const data = await getStations();
         setStations(data);
         if (data.length > 0) {
           setStationId(data[0].id); // Pre-select
         }
      } catch (error) {
         console.error('Erro ao buscar postos:', error);
      } finally {
         setFetchingStations(false);
      }
    };

    if (open) {
      loadStations();
    }
  }, [open]);

  const handleClear = () => {
    setName('');
    setAddress('');
    setStationId('');
    setGasPumpsStr('');
  };

  const handleClose = () => {
    handleClear();
  };

  const handleSave = async () => {
    if (!name || (!address || address.includes('_')) || !stationId) {
       toast.error('Preencha os campos obrigatórios (Nome, IP válido e Posto).');
       return;
    }

    // Convert "09, 10" safely to ["09", "10"]
    const parsedPumps = gasPumpsStr
       .split(',')
       .map((p) => p.trim())
       .filter((p) => p.length > 0);

    try {
       await createPrinterFx({
          name,
          address,
          stationId,
          gasPumps: parsedPumps,
       });
       toast.success('Impressora criada com sucesso!');
       handleClear();
       setOpen(false); // Fechar on success
    } catch (e) {
       toast.error('Erro ao criar impressora');
    }
  };

  const modalBody = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
       <TextField
          fullWidth
          label="Nome"
          variant="outlined"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ex: Impressora Pista 1"
          disabled={loading}
       />

       <TextField
          fullWidth
          label="Endereço IP"
          variant="outlined"
          placeholder="Ex: 192.168.000.010"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          disabled={loading}
       />

       <TextField
          fullWidth
          label="Bombas (separar por vírgula)"
          variant="outlined"
          value={gasPumpsStr}
          onChange={(e) => setGasPumpsStr(e.target.value)}
          placeholder="Ex: 09, 10, 11"
          disabled={loading}
       />

       <TextField
          select
          fullWidth
          label="Posto"
          variant="outlined"
          value={stationId}
          onChange={(e) => setStationId(e.target.value)}
          disabled={fetchingStations || loading}
       >
          {fetchingStations ? (
             <MenuItem value="">Carregando...</MenuItem>
          ) : (
             stations.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                   {s.name}
                </MenuItem>
             ))
          )}
       </TextField>
    </Box>
  );

  return (
    <SimpleModal
       onConfirm={handleSave}
       onClose={handleClose}
       open={open}
       setOpen={setOpen}
       Component={TriggerComponent}
       title="Cadastrar Impressora"
       confirmText="Salvar"
       cancelText="Cancelar"
       body={modalBody}
       maxWidth="sm"
    />
  );
};

export default CreatePrinterModal;
